/**
 * <product-card> — galería Embla en la foto + cambio de color sin navegar.
 *
 * SIN JS: la primera foto, el enlace al producto y las muestras como enlaces a
 * los hermanos (`grupo_color`). La pista es scroll-snap, así que un gesto
 * horizontal ya recorre las fotos.
 *
 * CON JS: la librería se pide en el boot (`precalentar`) y el constructor corre
 * cuando la card ya intersecta, no en el primer swipe. hover / `pointerup` /
 * `focusin` son el fallback si el IO no llegó.
 *
 * FUERA DE VISTA: no se monta. Al salir se destruye. Pestaña oculta: la cola
 * se pausa.
 *
 * CON EL DEDO NO HAY EMBLA (`#galeriaNativa()`): en la rejilla la galería es el
 * scroll-snap nativo —gesto, inercia y encaje del navegador, como en mammut.com— y el
 * paginador se sigue del `scrollLeft`. EL BUCLE SE CONSERVA con `bucleNativo` (`core/embla`):
 * el juego entero de fotos clonado a cada lado y una recolocación invisible al asentar.
 *
 * DENTRO DE UN RIEL Y CON EL DEDO: ni galería. Dos carruseles horizontales no caben en
 * un dedo, así que el gesto es del riel. Con ratón sí monta —el arrastre es preciso y
 * el riel tiene sus propios controles—, y en la rejilla se queda siempre.
 * Ver `#sinGaleria()`.
 */

import {
  cargarEmbla,
  cargarClassNames,
  opcionesDesde,
  montar,
  instante,
  precalentar,
  arrastrandoCarrusel,
  bucleNativo,
} from "core/embla";

const MOVIL = window.matchMedia("(max-width: 749px)");

/**
 * «El dedo es el puntero principal». Es la pregunta exacta, no un ancho de pantalla:
 * el conflicto de arrastres solo existe donde se navega arrastrando, y una ventana
 * estrecha en un portátil no es un móvil.
 */
const TACTIL = window.matchMedia("(pointer: coarse)");

/**
 * Cuánto tiene que quedarse el puntero sobre una tarjeta para que se considere
 * intención de mirarla y no un simple paso de largo. Ver `#onPointerEnter`.
 */
const INTENCION_MS = 90;

/**
 * COLA DE MONTAJE: burst de las que YA intersectan, y solo con la librería
 * parseada. El primer pantallazo (4–12 cards) cabe en uno o dos fotogramas;
 * no se gotea una por rAF ni se espera idle 400 ms. Offscreen no entra.
 *
 * No se drena hasta que `cargarEmbla()` resuelve: si N `#activar()`
 * esperaran el mismo `import` y construyeran al aterrizar, el primer swipe
 * pagaría el parse. Si la descarga falla, se reintenta (el memo de
 * `core/embla` se descarta). Pestaña oculta: se pausa.
 *
 * @type {Set<ProductCard>}
 */
const colaMontaje = new Set();
let drenandoCola = false;
let libreriaLista = false;
let pidiendoLibreria = false;
let reintentosLibreria = 0;

/** Visibles por fotograma: un pantallazo, no cincuenta offscreen. */
const BURST_POR_FRAME = 8;

/** @param {ProductCard} card */
function encolarMontaje(card) {
  if (card.embla) return;
  colaMontaje.add(card);
  if (libreriaLista) drenarCola();
}

/** @param {ProductCard} card */
function sacarMontaje(card) {
  colaMontaje.delete(card);
}

function marcarLibreriaLista() {
  if (libreriaLista) return;
  libreriaLista = true;
  drenarCola();
}

function pedirLibreria() {
  if (libreriaLista || pidiendoLibreria) return;
  pidiendoLibreria = true;
  precalentar();
  void cargarEmbla()
    .then(() => marcarLibreriaLista())
    .catch(() => {
      pidiendoLibreria = false;
      if (reintentosLibreria >= 2) return;
      reintentosLibreria += 1;
      window.setTimeout(pedirLibreria, 400 * reintentosLibreria);
    });
}

/** Margen alineado al IO de vista (100px) + un poco, para el enqueue eager. */
const MARGEN_VISTA = 120;
const IO_VISTA_MARGIN = "100px";

/** @param {Element} el */
function cercaDelViewport(el) {
  const r = el.getBoundingClientRect();
  return (
    r.width > 0 &&
    r.height > 0 &&
    r.bottom > -MARGEN_VISTA &&
    r.top < window.innerHeight + MARGEN_VISTA &&
    r.right > -MARGEN_VISTA &&
    r.left < window.innerWidth + MARGEN_VISTA
  );
}

/** @type {IntersectionObserver | null} */
let ioVista = null;

/** @param {ProductCard} card */
function observarVista(card) {
  if (!("IntersectionObserver" in window)) {
    encolarMontaje(card);
    return;
  }
  ioVista ??= new IntersectionObserver(
    (entradas) => {
      for (const entrada of entradas) {
        const el = entrada.target;
        if (typeof el.notificarVista === "function") el.notificarVista(entrada.isIntersecting);
      }
    },
    { rootMargin: IO_VISTA_MARGIN },
  );
  ioVista.observe(card);
}

/** @param {ProductCard} card */
function olvidarVista(card) {
  ioVista?.unobserve(card);
}

function drenarCola() {
  if (drenandoCola || !colaMontaje.size || !libreriaLista) return;
  if (document.hidden) {
    document.addEventListener(
      "visibilitychange",
      () => {
        if (!document.hidden) drenarCola();
      },
      { once: true },
    );
    return;
  }
  drenandoCola = true;
  const paso = () => {
    if (document.hidden) {
      drenandoCola = false;
      drenarCola();
      return;
    }
    let n = 0;
    while (colaMontaje.size && n < BURST_POR_FRAME) {
      const siguiente = colaMontaje.values().next().value;
      colaMontaje.delete(siguiente);
      siguiente.activarDesdeCola();
      n += 1;
    }
    if (colaMontaje.size) requestAnimationFrame(paso);
    else drenandoCola = false;
  };
  requestAnimationFrame(paso);
}

/**
 * Remedir las galerías vivas.
 *
 * `watchResize: false` apaga el ResizeObserver por instancia (N cards ×
 * viewport + cada slide). Hace falta remedir: si la card nace a 4 columnas y
 * pasa a 5/6, Embla se queda el slideSize viejo y el `translate` corre la foto
 * fuera del recorte. Un window `resize` NO dispara al cambiar `--plp-cols`.
 *
 * Un solo observer del viewport + el evento `plp:cols` (selector de densidad)
 * cubren esos cambios sin N handlers.
 *
 * @type {Set<ProductCard>}
 */
const vivas = new Set();
/** @type {ResizeObserver | null} */
let roCards = null;
/** @type {Set<ProductCard>} */
const pendientes = new Set();
let resizeRaf = 0;
let resizeTimer = 0;
let resizeOn = false;

function remedirVivas() {
  for (const card of vivas) {
    const vp = card.querySelector("[data-card-gallery-viewport]");
    if ((vp?.clientWidth ?? 0) < 2) continue;
    card.embla?.reInit();
  }
  pendientes.clear();
}

function programarRemedida() {
  if (resizeRaf) return;
  resizeRaf = requestAnimationFrame(() => {
    resizeRaf = 0;
    for (const card of pendientes) {
      pendientes.delete(card);
      if (!vivas.has(card) || !card.embla) continue;
      const vp = card.querySelector("[data-card-gallery-viewport]");
      if ((vp?.clientWidth ?? 0) < 2) continue;
      card.embla.reInit();
    }
  });
}

/** ¿El motor sigue midiendo otro ancho que el viewport actual? */
function emblaDesfasado(card, width) {
  const measured = card.embla?.internalEngine?.()?.containerRect?.width;
  return measured == null || Math.abs(measured - width) >= 0.5;
}

/** @param {ResizeObserverEntry[]} entries */
function onViewportResize(entries) {
  for (const entry of entries) {
    const w = entry.contentRect.width;
    if (w < 2 || entry.contentRect.height < 2) continue;
    const card = entry.target.closest("product-card");
    if (card?.embla && emblaDesfasado(card, w)) pendientes.add(card);
  }
  if (pendientes.size) programarRemedida();
}

function onResizeCards() {
  clearTimeout(resizeTimer);
  resizeTimer = window.setTimeout(remedirVivas, 150);
}

/** @param {ProductCard} card */
function vigilarResize(card) {
  vivas.add(card);
  const vp = card.querySelector("[data-card-gallery-viewport]");
  if (vp && "ResizeObserver" in window) {
    roCards ??= new ResizeObserver(onViewportResize);
    roCards.observe(vp);
    if (resizeOn) return;
    resizeOn = true;
    window.addEventListener("plp:cols", remedirVivas);
    return;
  }
  if (resizeOn) return;
  resizeOn = true;
  window.addEventListener("resize", onResizeCards, { passive: true });
  window.addEventListener("plp:cols", remedirVivas);
}

/** @param {ProductCard} card */
function olvidarResize(card) {
  const vp = card.querySelector("[data-card-gallery-viewport]");
  if (vp) roCards?.unobserve(vp);
  pendientes.delete(card);
  vivas.delete(card);
  if (vivas.size) return;
  resizeOn = false;
  window.removeEventListener("resize", onResizeCards);
  window.removeEventListener("plp:cols", remedirVivas);
  clearTimeout(resizeTimer);
  if (resizeRaf) {
    cancelAnimationFrame(resizeRaf);
    resizeRaf = 0;
  }
  roCards?.disconnect();
  roCards = null;
}

class ProductCard extends HTMLElement {
  /** @type {any} */
  #embla = null;
  #desconectado = false;
  /** @type {AbortController | null} */
  #abort = null;
  /** Invalida un `#activar()` en vuelo si se destruye o se cambia de color. */
  #gen = 0;
  /** Evita dos montajes si hover y pointerdown disparan a la vez. */
  #montando = false;
  /** @type {Promise<void> | null} */
  #activarPromise = null;
  /** @type {HTMLElement | null} */
  #gallery = null;
  /** @type {HTMLElement | null} */
  #track = null;
  /** @type {HTMLElement | null} */
  #viewport = null;
  /** @type {object[] | null} */
  #siblings = null;
  /** @type {(() => void) | null} */
  #onMq = null;
  /** Temporizador de intención de hover. @type {number | null} */
  #intencion = null;
  /** Listeners de hover/foco de la galería ya puestos. */
  #galeriaLista = false;
  /** @type {(() => void) | null} */
  #onPuntero = null;
  /** El paginador ya sigue al scroll nativo (listener puesto una vez por conexión). */
  #nativaLista = false;
  /** @type {ReturnType<typeof bucleNativo> | null} bucle del scroll nativo (dedo) */
  #bucle = null;
  /**
   * Foto real en la que está (o debe estar) la galería nativa. Se guarda aparte del
   * `scrollLeft` porque este puede no valer nada: una card que se conecta con la caja a
   * 0 px (panel oculto, pestaña, fragmento AJAX) no se puede colocar hasta que mide, y
   * cuando mide hay que saber a qué foto volver. Lo actualiza el asentado del scroll.
   */
  #indiceReal = 0;
  /** @type {ResizeObserver | null} recoloca la galería nativa cuando la caja cambia de tamaño */
  #roNativo = null;

  connectedCallback() {
    this.#desconectado = false;
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#gallery = this.querySelector("[data-card-gallery]");
    this.#viewport = this.#gallery?.querySelector("[data-card-gallery-viewport]");
    this.#track = this.#gallery?.querySelector("[data-card-gallery-track]");
    this.#siblings = this.#leerHermanos();

    this.addEventListener("click", (event) => this.#onClick(event), { signal });

    const onPunteroGaleria = (event) => {
      // Puntero grueso: o no hay galería (dentro del riel) o es el scroll-snap
      // nativo, y en los dos casos el gesto es del navegador. Ver `#sinGaleria()`.
      if (this.#sinGaleria() || this.#galeriaNativa()) return;

      // CORTAR SOLO SI HAY QUIEN USE EL GESTO. Antes se cortaba siempre, y en el
      // PRIMER arrastre —cuando el motor de la tarjeta todavía no está montado— el
      // gesto se quedaba en tierra de nadie: medido en el riel de la home a 1440px,
      // ni la tarjeta ni el riel se movían un píxel. Cortar es quitárselo al riel;
      // solo se justifica cuando esta galería puede quedárselo.
      //
      // En táctil hay que cortar `touchstart` y `pointerdown`: si no, el
      // padre sigue oyendo el toque y el riel salta con la foto.
      if (this.#embla) {
        event.stopPropagation();
        return;
      }

      // NO MONTAR A MITAD DE ARRASTRE. Si el constructor corre mientras el
      // dedo ya mueve el scroll-snap nativo, el primer gesto se come. El camino
      // principal ya montó al intersectar; esto es el fallback: el arrastre de
      // ahora se queda nativo y Embla entra al soltar.
      if (event.type !== "pointerdown") return;
      const montarAlSoltar = () => this.#onPrimeraInteraccion();
      this.addEventListener("pointerup", montarAlSoltar, { once: true, signal });
      this.addEventListener("pointercancel", montarAlSoltar, { once: true, signal });
    };
    this.#gallery?.addEventListener("pointerdown", onPunteroGaleria, { signal });
    this.#gallery?.addEventListener("touchstart", onPunteroGaleria, { signal, passive: true });

    this.#onMq = () => this.#embla?.reInit();
    MOVIL.addEventListener("change", this.#onMq);
    this.#onPuntero = () => this.#reevaluarGaleria();
    TACTIL.addEventListener("change", this.#onPuntero);
    this.#reevaluarGaleria();
  }

  disconnectedCallback() {
    this.#desconectado = true;
    this.#cancelarIntencion();
    olvidarVista(this);
    this.#galeriaLista = false;
    this.#nativaLista = false;
    this.#roNativo?.disconnect();
    this.#roNativo = null;
    sacarMontaje(this);
    olvidarResize(this);
    this.#abort?.abort();
    if (this.#onMq) MOVIL.removeEventListener("change", this.#onMq);
    if (this.#onPuntero) TACTIL.removeEventListener("change", this.#onPuntero);
    this.#destruir();
  }

  /**
   * Observa el viewport y pide la librería. No-op si cede al riel o no hay 2+ fotos.
   * Se puede llamar otra vez si el puntero deja de ser grueso.
   */
  #prepararGaleria() {
    if (this.#sinGaleria()) return;
    if (!this.#gallery || this.#slides().length < 2) return;
    if (this.#galeriaNativa()) {
      this.#prepararNativa();
      return;
    }

    this.#mostrarProgreso(true);
    this.#mostrarNav(true);
    pedirLibreria();
    this.#preloadAdyacentes(0);
    observarVista(this);
    // Eager: si ya está en pantalla, no esperar el turno del IO.
    if (!document.hidden && cercaDelViewport(this)) encolarMontaje(this);
    if (this.#galeriaLista) return;
    this.#galeriaLista = true;
    const { signal } = this.#abort ?? {};
    this.addEventListener("pointerenter", () => this.#onPointerEnter(), { signal });
    this.addEventListener("pointerleave", () => this.#cancelarIntencion(), { signal });
    this.addEventListener("focusin", () => this.#onPrimeraInteraccion(), { signal });
  }

  /**
   * Galería NATIVA (dedo, fuera de un riel): sin librería, sin cola, sin IO. Queda el
   * paginador, que sigue al `scrollLeft` del viewport, la precarga de las vecinas y el
   * bucle por clones. Las flechas son de ratón y se retiran.
   */
  #prepararNativa() {
    this.#mostrarProgreso(true);
    this.#mostrarNav(false);
    if (!this.#nativaLista && this.#viewport && this.#track) {
      this.#nativaLista = true;
      const signal = this.#abort?.signal;
      this.#bucle = bucleNativo({
        viewport: this.#viewport,
        pista: this.#track,
        slides: () => this.#slides(),
        marcarClon: (clon, vecino) => {
          // No es un slide (`#slides()` mira `data-card-slide`), no lo lee nadie ni recibe
          // foco. NO va `inert`: es un enlace a la misma ficha, y si un tap cae sobre un
          // clon —se ve un instante antes de recolocar— tiene que abrir el producto igual.
          clon.removeAttribute("data-card-slide");
          clon.setAttribute("data-card-slide-clon", "");
          clon.setAttribute("aria-hidden", "true");
          clon.tabIndex = -1;
          if (vecino) {
            clon.querySelectorAll("img").forEach((img) => {
              img.loading = "eager";
            });
          }
        },
        alAsentar: (i) => {
          this.#indiceReal = i;
          this.#sincronizarProgreso();
          this.#preloadAdyacentes(i);
        },
        signal,
      });
      this.#viewport.addEventListener(
        "scroll",
        () => {
          if (this.#embla) return;
          this.#sincronizarProgreso();
        },
        { passive: true, signal },
      );
      // LA CAJA PUEDE MEDIR 0 AL CONECTAR (panel oculto, pestaña, fragmento AJAX): entonces
      // ni se pueden poner los clones ni colocar nada, y quedaría a la vista lo primero de la
      // pista. Cuando la caja gana tamaño, o cambia de columna, se vuelve a la foto real.
      if ("ResizeObserver" in window) {
        this.#roNativo = new ResizeObserver(() => this.#recolocar());
        this.#roNativo.observe(this.#viewport);
      }
    }
    this.#ponerClones();
    this.#preloadAdyacentes(this.#indiceNativo());
    this.#sincronizarProgreso();
  }

  /** Pone el bucle (si la caja ya mide) y coloca la foto real que toca; se verifica al frame siguiente. */
  #ponerClones() {
    if (!this.#bucle || this.#embla) return;
    if (this.#bucle.poner()) this.#recolocar();
    requestAnimationFrame(() => this.#recolocar());
  }

  /**
   * Deja el scroll sobre `#indiceReal`. Idempotente y barato: al poner clones, en el frame
   * siguiente y cada vez que la caja cambia de tamaño (si aún no había clones porque la
   * caja medía 0, es aquí donde se ponen).
   */
  #recolocar() {
    const bucle = this.#bucle;
    if (!bucle || this.#embla || !this.#viewport || this.#viewport.clientWidth < 2) return;
    if (!bucle.activo() && !bucle.poner()) return;
    bucle.colocar(this.#indiceReal, "instant", true);
  }

  /** Quita los clones y deja el scroll sobre la misma foto real. */
  #quitarClones() {
    this.#bucle?.quitar();
  }


  /** Sin galería: fuera instancia, fuera cola, fuera IO, fuera controles. */
  #cederGaleria() {
    this.#cancelarIntencion();
    olvidarVista(this);
    sacarMontaje(this);
    this.#destruir();
    this.#mostrarNav(false);
    this.#mostrarProgreso(false);
  }

  /**
   * Lo llama el IO compartido. Visible: encola. Fuera: destruye y sigue
   * observada para remontar al volver.
   * @param {boolean} visible
   */
  notificarVista(visible) {
    if (this.#sinGaleria() || this.#galeriaNativa()) return;
    if (visible) {
      this.#preloadAdyacentes(0);
      encolarMontaje(this);
      return;
    }
    sacarMontaje(this);
    if (this.#embla || this.#montando) this.#destruir();
  }

  /**
   * Lo llama la cola. Offscreen, con el dedo o a mitad de un arrastre: no monta
   * (el IO reencola al volver; el arrastre se reintenta en el siguiente turno).
   */
  activarDesdeCola() {
    if (this.#desconectado || !this.isConnected || this.#embla || this.#montando || this.#sinGaleria() || this.#galeriaNativa()) {
      return;
    }
    if (this.#arrastreEnCurso()) {
      encolarMontaje(this);
      return;
    }
    const r = this.getBoundingClientRect();
    if (r.width < 2 || r.height < 2) return;
    void this.#activar();
  }

  /** Instancia de Embla, o null. */
  get embla() {
    return this.#embla;
  }

  #slides() {
    return [...(this.#track?.querySelectorAll("[data-card-slide]") ?? [])];
  }

  #indiceNativo() {
    const vp = this.#viewport;
    if (!vp) return 0;
    const w = vp.clientWidth;
    if (w < 2) return 0;
    if (this.#bucle?.activo()) return this.#bucle.indice();
    return Math.round(vp.scrollLeft / w);
  }

  #leerHermanos() {
    const script = this.querySelector("[data-card-siblings]");
    if (!script) return null;
    try {
      const data = JSON.parse(script.textContent ?? "[]");
      return Array.isArray(data) && data.length > 1 ? data : null;
    } catch {
      return null;
    }
  }

  /**
   * PASAR POR ENCIMA NO ES QUERER MIRAR.
   *
   * `pointerenter` salta en CADA tarjeta que cruza el ratón, y montar era inmediato:
   * medido en la PLP, barrer el puntero por la rejilla dejaba 21 instancias
   * vivas —~9 ms de hilo principal cada una, 158 ms en veinte tarjetas— más su
   * listener de `resize`. Aquí eran decenas.
   *
   * Así que el hover espera a que el puntero SE QUEDE. `pointerleave` cancela, y el
   * barrido pasa de veinte montajes a cero. El camino principal ya montó las
   * visibles al intersectar; esto es el fallback si el IO no ha llegado.
   *
   * Los gestos que sí son intención no esperan nada: `pointerup` y `focusin` montan
   * en el acto, y pulsar una flecha entra por `#paso`, que ya espera a `#activar()`.
   * Hasta que monta, la pista sigue siendo scroll-snap nativo: no hay ventana muerta.
   */
  #onPointerEnter() {
    if (this.#embla || this.#montando || this.#intencion !== null) return;
    this.#intencion = window.setTimeout(() => {
      this.#intencion = null;
      this.#onPrimeraInteraccion();
    }, INTENCION_MS);
  }

  #cancelarIntencion() {
    if (this.#intencion === null) return;
    clearTimeout(this.#intencion);
    this.#intencion = null;
  }

  #onPrimeraInteraccion() {
    this.#cancelarIntencion();
    sacarMontaje(this);
    if (this.#embla || this.#montando) return;
    this.#preloadAdyacentes(0);
    void this.#activar();
  }

  /**
   * La primera foto va
   * `eager` (LCP); el resto `lazy` hasta que hace falta. Al interactuar (y en
   * cada `select`) se marcan eager la actual y las dos vecinas del loop.
   * @param {number} i
   */
  #preloadAdyacentes(i) {
    const slides = this.#slides();
    const n = slides.length;
    if (n < 2) return;
    const idxs = new Set([i, (i + 1) % n, (i - 1 + n) % n]);
    for (const j of idxs) {
      const img = slides[j]?.querySelector("img");
      if (!(img instanceof HTMLImageElement)) continue;
      if (img.loading === "lazy") img.loading = "eager";
      // `decode()` es la vía del estándar para no pagar el decode en el primer
      // frame que enseña la foto. Sin esto, el primer swipe a la vecina hitchaba
      // aunque el riel ya estuviera montado.
      if (typeof img.decode === "function") void img.decode().catch(() => {});
    }
  }

  /** @param {Event} event */
  #onClick(event) {
    if (!(event.target instanceof Element)) return;

    const prev = event.target.closest("[data-card-gallery-prev]");
    if (prev && this.contains(prev)) {
      event.preventDefault();
      void this.#paso(-1);
      return;
    }
    const next = event.target.closest("[data-card-gallery-next]");
    if (next && this.contains(next)) {
      event.preventDefault();
      void this.#paso(1);
      return;
    }

    if (event.defaultPrevented) return;

    const chip = event.target.closest("[data-card-color]");
    if (!chip || !this.#siblings || !this.contains(chip)) return;
    const handle = chip instanceof HTMLElement ? chip.dataset.handle : "";
    if (!handle) return;
    const sibling = this.#siblings.find((s) => s.handle === handle);
    if (!sibling) return;
    event.preventDefault();
    void this.#aplicarHermano(sibling, chip);
  }

  /**
   * DOS CARRUSELES HORIZONTALES NO CABEN EN UN DEDO.
   *
   * La galería de la foto se retira cuando —y SOLO cuando— la tarjeta vive dentro de
   * un riel (`<enova-carousel>`) y se navega con el dedo. Ahí el gesto de pasar fotos
   * y el de recorrer el catálogo son EL MISMO —arrastrar en horizontal— y no hay eje
   * ni umbral que los distinga: medido en la home a 390px, un swipe movía LOS DOS a
   * la vez. Se intentó repartirlo por slide (la `is-selected` se quedaba el gesto, el
   * peek cedía) y seguía siendo ambiguo: la misma superficie hacía dos cosas según
   * qué tarjeta tocaras. Con el riel, el riel.
   *
   * CON RATÓN, DENTRO DEL RIEL, LA GALERÍA SE QUEDA. No hay conflicto que resolver: el
   * puntero es preciso, las flechas de la card aparecen al pasar por encima de ESA
   * tarjeta y el riel tiene sus propios mandos en la cabecera. Quitarla ahí sería
   * perder fotos sin ganar nada.
   *
   * EN LA REJILLA DE LA PLP, TAMPOCO SE RETIRA. Allí no hay riel con quien competir: la
   * página se desplaza en vertical y la foto en horizontal, que son ejes distintos y el
   * navegador ya sabe separarlos. La galería se queda —con su paginador de segmentos,
   * que es lo que anuncia que hay más fotos—.
   *
   * La pregunta es el PUNTERO, no el ancho: una ventana estrecha en un portátil no es
   * un móvil, y el conflicto solo existe donde se navega arrastrando.
   *
   * Consecuencias en cascada, todas cableadas a este predicado: no se monta Embla, no
   * se encola, no se observa, y se ocultan flechas y paginador. El CSS hace su mitad
   * en `card.css`, bloque `(pointer: coarse)`.
   */
  #sinGaleria() {
    return TACTIL.matches && this.closest("enova-carousel") !== null;
  }

  /**
   * Con el dedo la galería es el scroll-snap del navegador y Embla no monta. Es la
   * misma decisión que toman los rieles del tema (`rielNativo()` en `core/embla`);
   * aquí se lee el mismo MQL, que ya vigila `#reevaluarGaleria()`.
   */
  #galeriaNativa() {
    return TACTIL.matches;
  }

  /**
   * Hay un arrastre de carrusel EN CURSO. No se monta a mitad de gesto: el
   * constructor entra en medio y el arrastre se queda a medias; la cola lo reintenta.
   *
   * La marca la pone `core/embla` en el `<html>` (`data-carousel-dragging`) al
   * primer movimiento con el puntero abajo. Antes esto miraba una clase
   * `is-dragging` en el viewport del riel que no escribe nadie, así que la guarda
   * nunca saltaba.
   */
  #arrastreEnCurso() {
    return arrastrandoCarrusel();
  }

  /** Reevalúa si esta tarjeta tiene galería, y de qué tipo (cambió el puntero). */
  #reevaluarGaleria() {
    if (this.#sinGaleria()) {
      this.#cederGaleria();
      return;
    }
    if (this.#galeriaNativa()) {
      // De ratón a dedo: fuera Embla, la pista vuelve a ser el scroller CSS.
      this.#cancelarIntencion();
      olvidarVista(this);
      sacarMontaje(this);
      this.#destruir();
    }
    this.#prepararGaleria();
  }

  async #activar() {
    if (this.#sinGaleria() || this.#galeriaNativa()) return;
    if (this.#arrastreEnCurso()) {
      encolarMontaje(this);
      return;
    }
    if (this.#embla || this.#desconectado || !this.#track || !this.#gallery) return;
    if (this.#slides().length < 2) return;
    if (this.#activarPromise) return this.#activarPromise;

    const gen = this.#gen;
    this.#montando = true;
    this.#activarPromise = this.#montarEmbla(gen);
    try {
      await this.#activarPromise;
    } finally {
      if (this.#gen === gen) this.#activarPromise = null;
      this.#montando = false;
    }
  }

  /** @param {number} gen */
  async #montarEmbla(gen) {
    const [EmblaCarousel, ClassNames] = await Promise.all([cargarEmbla(), cargarClassNames()]);
    if (gen !== this.#gen || this.#desconectado || this.#embla || !this.#track || !this.#gallery || !this.#viewport) {
      return;
    }
    if (this.#sinGaleria() || this.#galeriaNativa() || this.#slides().length < 2) return;

    const indice = this.#indiceNativo();
    this.#embla = montar(EmblaCarousel, this.#viewport, {
      host: this.#gallery,
      container: this.#track,
      slides: "[data-card-slide]",
      plugins: [ClassNames({ snapped: "is-selected", inView: false })],
      signal: this.#abort?.signal,
      opciones: {
        ...opcionesDesde(this.#gallery),
        loop: true,
        align: "start",
        containScroll: false,
        skipSnaps: false,
        dragFree: false,
        dragThreshold: 6,
        startIndex: indice,
        watchResize: false,
        duration: 29,
        watchDrag: () => !this.#sinGaleria(),
      },
    });

    const galeria = this.#gallery;
    const asentar = () => galeria.classList.add("is-settled");
    const mover = () => galeria.classList.remove("is-settled");
    asentar();
    this.#embla.on("settle", asentar);
    this.#embla.on("reInit", asentar);
    this.#embla.on("pointerDown", mover);
    this.#embla.on("select", () => {
      const engine = this.#embla?.internalEngine?.();
      const salto =
        engine && !engine.dragHandler.pointerDown() && engine.scrollBody.settled();
      if (salto) asentar();
      else mover();
      this.#sincronizarProgreso();
      this.#preloadAdyacentes(this.#embla?.selectedScrollSnap() ?? 0);
    });
    this.#mostrarNav(true);
    if ((this.#viewport?.clientHeight ?? 0) < 2) this.#embla.reInit();
    const n = this.#embla.slideNodes().length;
    const i = n ? ((indice % n) + n) % n : 0;
    if (i !== this.#embla.selectedScrollSnap()) this.#embla.scrollTo(i, true);
    vigilarResize(this);
    this.#sincronizarProgreso();
    this.#preloadAdyacentes(i);
  }

  /**
   * Un paso en la galería. `scrollNext`/`scrollPrev` toman el atajo del loop
   * (última → primera en un solo slide). Si Embla apagó `loop`, el wrap salta
   * para no rebobinar todo el riel.
   * @param {number} dir
   */
  async #paso(dir) {
    if (!this.#embla) await this.#activar();
    const api = this.#embla;
    if (!api) {
      // Galería nativa: un paso es una foto. Con bucle se va por la copia más cercana
      // (del último al primero, un paso corto) y se recoloca al asentar.
      const vp = this.#viewport;
      if (!vp || this.#slides().length < 2) return;
      if (this.#bucle?.activo()) {
        this.#bucle.colocar(this.#indiceReal + dir);
        return;
      }
      const max = vp.scrollWidth - vp.clientWidth;
      if (dir > 0 && vp.scrollLeft >= max - 1) vp.scrollTo({ left: 0 });
      else if (dir < 0 && vp.scrollLeft <= 1) vp.scrollTo({ left: max });
      else vp.scrollBy({ left: dir * vp.clientWidth });
      return;
    }
    const n = api.slideNodes().length;
    if (n < 2) return;
    const jump = instante();
    if (dir > 0) {
      if (api.canScrollNext()) api.scrollNext(jump);
      else api.scrollTo(0, true);
      return;
    }
    if (api.canScrollPrev()) api.scrollPrev(jump);
    else api.scrollTo(n - 1, true);
  }

  /** @param {boolean} on */
  #mostrarNav(on) {
    const nav = this.querySelector("[data-card-gallery-nav]");
    nav?.toggleAttribute("hidden", !(on && this.#slides().length > 1));
  }

  /** @param {boolean} on */
  #mostrarProgreso(on) {
    const progress = this.querySelector("[data-card-gallery-progress]");
    progress?.toggleAttribute("hidden", !(on && this.#slides().length > 1));
  }

  #sincronizarProgreso() {
    const i = this.#embla ? this.#embla.selectedScrollSnap() : this.#indiceNativo();
    this.querySelectorAll("[data-card-gallery-progress] > *").forEach((seg, n) => {
      seg.classList.toggle("is-active", n === i);
    });
  }

  #destruir() {
    this.#gen += 1;
    this.#montando = false;
    this.#activarPromise = null;
    this.#quitarClones();
    sacarMontaje(this);
    olvidarResize(this);
    this.#gallery?.classList.remove("is-settled");
    this.#embla?.destroy();
    this.#embla = null;
  }

  /**
   * @param {object} sibling
   * @param {Element} chip
   */
  async #aplicarHermano(sibling, chip) {
    this.#destruir();
    this.#pintarSlides(sibling);
    this.#pintarTexto(sibling);
    this.#pintarPrecio(sibling);
    this.#pintarBadges(sibling);
    this.#pintarEnlaces(sibling);
    this.#pintarWishlist(sibling);
    this.#pintarQuickAdd(sibling);
    this.querySelectorAll("[data-card-color]").forEach((el) => {
      const on = el === chip;
      el.classList.toggle("is-active", on);
      if (on) el.setAttribute("aria-current", "true");
      else el.removeAttribute("aria-current");
    });

    const muchas = (sibling.images?.length ?? 0) > 1;
    this.#mostrarProgreso(muchas);
    if (muchas) {
      this.#preloadAdyacentes(0);
      if (this.#galeriaNativa()) {
        // Pista nueva, misma caja: clones nuevos, en la primera foto, y el paginador repintado.
        this.#indiceReal = 0;
        this.#prepararNativa();
      } else {
        await this.#activar();
      }
    }
  }

  /** @param {object} sibling */
  #pintarSlides(sibling) {
    if (!this.#track) return;
    const images = Array.isArray(sibling.images) ? sibling.images : [];
    // Hermano sin ninguna foto: el hueco explicado, no un track vacío. Mismo elemento y
    // misma clase que emite la rama `{% else %}` de `pattern-card-product`; el dibujo lo
    // pone `card.css` en `.card__media-empty`, que es lo que permite que los dos caminos
    // se vean igual sin duplicar el glifo.
    this.#track.replaceChildren(
      ...(images.length
        ? images.map((img, i) => this.#crearSlide(sibling.url, img, i === 0))
        : [this.#crearHueco(sibling.url)]),
    );
    const progress = this.querySelector("[data-card-gallery-progress]");
    if (progress) {
      progress.replaceChildren(
        ...images.map((_, i) => {
          const span = document.createElement("span");
          if (i === 0) span.className = "is-active";
          return span;
        }),
      );
    }
  }

  /**
   * Hueco de «este color todavía no tiene foto». Ocupa el lienzo entero para que la
   * tarjeta siga midiendo lo que sus vecinas de la rejilla.
   * @param {string} href
   */
  #crearHueco(href) {
    const a = document.createElement("a");
    a.className = "card__media-link card__media-empty";
    a.href = href || "#";
    a.tabIndex = -1;
    a.setAttribute("aria-hidden", "true");
    return a;
  }

  /**
   * @param {string} href
   * @param {{ src?: string, srcset?: string, width?: number, height?: number }} img
   * @param {boolean} eager
   */
  #crearSlide(href, img, eager) {
    const a = document.createElement("a");
    a.className = "card__gallery-slide";
    a.href = href || "#";
    a.tabIndex = -1;
    a.setAttribute("aria-hidden", "true");
    a.dataset.cardSlide = "";
    const image = document.createElement("img");
    image.className = "card__media-primary";
    image.src = img.src ?? "";
    if (img.srcset) image.srcset = img.srcset;
    image.sizes = "(max-width: 768px) 50vw, 25vw";
    image.alt = "";
    if (img.width) image.width = img.width;
    if (img.height) image.height = img.height;
    image.loading = eager ? "eager" : "lazy";
    a.append(image);
    return a;
  }

  /** @param {object} sibling */
  #pintarTexto(sibling) {
    const title = this.querySelector(".card__title");
    if (title) title.textContent = sibling.title ?? "";
  }

  /**
   * Fila de tags promocionales del hermano. Vacío esconde el contenedor (una fila
   * hueca descuadra la rejilla). El HTML lo resolvió Liquid con los mismos snippets
   * que la tarjeta: campaña + `enova.badges` + agotado/bestseller.
   * @param {object} sibling
   */
  #pintarBadges(sibling) {
    const html = typeof sibling.badges_html === "string" ? sibling.badges_html.trim() : "";
    let row = this.querySelector(".card__badges");
    if (!html) {
      if (row) {
        row.replaceChildren();
        row.setAttribute("hidden", "");
      }
      return;
    }
    if (!row) {
      row = document.createElement("div");
      row.className = "card__badges";
      const before = this.querySelector(".card__link");
      if (before) this.insertBefore(row, before);
      else this.append(row);
    }
    row.removeAttribute("hidden");
    row.innerHTML = html;
  }

  /** @param {object} sibling */
  #pintarPrecio(sibling) {
    const wrap = this.querySelector(".card__pricing");
    if (!wrap) return;
    const price = wrap.querySelector("[data-price], .price");
    if (price && sibling.price_html) {
      const tmp = document.createElement("div");
      tmp.innerHTML = sibling.price_html;
      const next = tmp.querySelector("[data-price], .price");
      if (next) price.replaceWith(next);
    }
    let badge = wrap.querySelector(".card__badge--discount");
    if (sibling.pct_off > 0 && sibling.discount_badge) {
      if (!badge) {
        badge = document.createElement("span");
        badge.className = "card__badge card__badge--discount";
        wrap.append(badge);
      }
      badge.textContent = sibling.discount_badge;
      badge.removeAttribute("hidden");
    } else if (badge) {
      badge.textContent = "";
      badge.setAttribute("hidden", "");
    }
  }

  /** @param {object} sibling */
  #pintarEnlaces(sibling) {
    const url = sibling.url;
    if (!url) return;
    this.querySelector(".card__link")?.setAttribute("href", url);
    this.querySelectorAll("[data-card-slide], .card__buy-add--link").forEach((a) => {
      if (a instanceof HTMLAnchorElement) a.href = url;
    });
  }

  /** @param {object} sibling */
  #pintarWishlist(sibling) {
    const heart = this.querySelector("[data-wishlist-toggle]");
    if (!(heart instanceof HTMLElement) || !sibling.handle) return;
    heart.dataset.handle = sibling.handle;
    if (sibling.wishlist_add) heart.dataset.labelAdd = sibling.wishlist_add;
    if (sibling.wishlist_remove) heart.dataset.labelRemove = sibling.wishlist_remove;
    void import("core/wishlist").then((m) => m.syncWishlistUi(this));
  }

  /** @param {object} sibling */
  #pintarQuickAdd(sibling) {
    const btn = this.querySelector("[data-quick-add]");
    if (!(btn instanceof HTMLElement) || !sibling.variant_id) return;
    btn.dataset.variantId = String(sibling.variant_id);
    if (sibling.regular_price) {
      btn.dataset.regularPrice = String(sibling.regular_price);
    } else {
      delete btn.dataset.regularPrice;
    }
  }
}

if (!customElements.get("product-card")) {
  customElements.define("product-card", ProductCard);
}

export { ProductCard };
